import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import { getTouristSpots } from "@/services/publicApi";
import type { TouristSpot } from "@/types";

export default function AdminTouristSpots() {
  const [rows, setRows] = useState<TouristSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getTouristSpots()
      .then((s) => active && setRows(s))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => !q || r.name.toLowerCase().includes(q) || r.locationName.toLowerCase().includes(q));
  }, [rows, query]);

  const columns: DataColumn<TouristSpot>[] = [
    {
      key: "name",
      header: "Spot",
      render: (r) => (
        <div className="flex items-center gap-3">
          <img src={r.image} alt={r.name} className="h-11 w-16 shrink-0 rounded-md object-cover" />
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground-950">{r.name}</p>
            <p className="truncate text-xs text-foreground-500">/{r.slug}</p>
          </div>
        </div>
      ),
    },
    { key: "locationName", header: "Location", render: (r) => <span className="text-foreground-700">{r.locationName}</span>, nowrap: true },
    { key: "entryFee", header: "Entry Fee", render: (r) => <span className="text-foreground-700">{r.entryFee}</span>, nowrap: true },
    { key: "timings", header: "Timings", render: (r) => <span className="text-foreground-500">{r.timings}</span>, nowrap: true },
    {
      key: "rating",
      header: "Rating",
      align: "center",
      render: (r) => (
        <span className="inline-flex items-center gap-1 font-semibold text-foreground-900">
          <i className="ri-star-fill text-primary-500"></i> {r.rating}
        </span>
      ),
      nowrap: true,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <Link
            to={`/spot/${r.slug}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="View spot"
          >
            <i className="ri-external-link-line"></i>
          </Link>
          <button
            type="button"
            onClick={() => flash(`Editing “${r.name}”…`)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit spot"
          >
            <i className="ri-edit-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="Tourist Spots" path="/admin/tourist-spots" />

      <AdminPageHeader
        icon="ri-camera-lens-line"
        title="Tourist spots"
        subtitle="Curate the attractions shown across the tourist-spot pages."
        actions={
          <>
            {notice && (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            )}
            <button
              type="button"
              onClick={() => flash("Opening the new-spot form…")}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i> Add spot
            </button>
          </>
        }
      />

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search tourist spots…" />
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load tourist spots. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="900px" />
        )}
      </div>
    </>
  );
}